import { Field, Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { db } from '../instant';
import { id, TransactionChunk } from '@instantdb/core';
import { Button, Stack, Typography, Box, ToggleButton, SxProps, Theme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useState, useMemo } from 'react';
import FormTextInput from './FormTextInput';
import DiscardChangesDialog from './DiscardChangesDialog';
import DirtyChecker from './DirtyChecker';
import { eventGroupFormSchema, EventGroupFormValues } from 'src/schema';
import { getOneLink } from 'src/utils';
import EventSelectorList from './EventSelectorList';
import { AppSchema } from '@zw-app/shared/instant.schema';

interface Props {
    groupId?: string;
    viewOnly?: boolean;
    inTab?: boolean;
}

export default function EventGroupForm(props: Props) {
    const { groupId, viewOnly = false, inTab = false } = props;
    const navigate = useNavigate();
    const [editMode, setEditMode] = useState(!groupId);
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const isCreating = groupId === undefined;

    // Memoize targetGroupId to prevent infinite re-renders when creating a new group
    const targetGroupId = useMemo(() => groupId || id(), [groupId]);

    // Always query eventGroups to maintain consistent query structure
    // When creating (no groupId), targetGroupId is a new id that won't match, so eventGroups will be empty
    const { data, isLoading, error } = db.useQuery({
        eventGroups: {
            $: {
                where: {
                    id: targetGroupId,
                },
            },
            events: {
                $: { order: { order: 'asc' } },
            },
        },
        events: {},
    });

    const group = getOneLink(data?.eventGroups);
    const maxOrder = data?.events?.reduce((max, event) => Math.max(max, event.order || 0), 0) || 0;

    // Memoize initialValues to prevent unnecessary Formik reinitializations
    // Must be called before any early returns to satisfy React Hooks rules
    const initialValues: EventGroupFormValues = useMemo(
        () =>
            group
                ? {
                      groupName: group.name,
                      eventIds: group.events?.map(event => event.id) || [],
                  }
                : {
                      groupName: '',
                      eventIds: [],
                  },
        [group]
    );

    const [hasBeenEdited, setHasBeenEdited] = useState(false);

    const discard = useCallback(() => {
        setEditMode(false);
        setHasBeenEdited(false);
    }, [setEditMode, setHasBeenEdited]);

    const closeDiscardDialog = useCallback(() => {
        setDiscardDialogOpen(false);
    }, [setDiscardDialogOpen]);

    const toggleEditMode = useCallback(() => {
        if (editMode && hasBeenEdited) {
            setDiscardDialogOpen(true);
        } else {
            setEditMode(!editMode);
        }
    }, [editMode, hasBeenEdited, setEditMode, setDiscardDialogOpen]);

    if (isLoading) return <div>Authenticating...</div>;
    if (error) return <div>Error authenticating: {error.message}</div>;

    const canEdit = !isCreating && !viewOnly;

    const subtitle = isCreating ? 'Create Group' : editMode ? 'Edit Group' : 'Group Info';

    const containerSx: SxProps<Theme> = inTab
        ? {
              position: 'relative',
              boxSizing: 'border-box',
              width: '100%',
          }
        : {
              position: 'relative',
              boxSizing: 'border-box',
              width: '100%',
          };

    const content = (
        <>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                {!inTab && (
                    <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        {subtitle}
                    </Typography>
                )}
                {canEdit && (
                    <ToggleButton value="edit-mode" selected={editMode} onClick={toggleEditMode}>
                        <EditIcon />
                    </ToggleButton>
                )}
            </Box>
            <Formik
                initialValues={initialValues}
                // If events change in DB, update the form values
                // e.g. when deleting maps, or after saving changes
                enableReinitialize
                validationSchema={eventGroupFormSchema}
                onSubmit={async values => {
                    const txs: TransactionChunk<AppSchema, 'events' | 'eventGroups'>[] = [
                        db.tx.eventGroups[targetGroupId]
                            .update({
                                name: values.groupName,
                            })
                            .link({
                                events: values.eventIds,
                            }),
                    ];

                    // Update event orders
                    // TODO: This will leave gaps in the unsorted event orders.
                    // Should be re-indexed when deleting groups or ungrouping events.
                    for (let i = 0; i < values.eventIds.length; i++) {
                        txs.push(
                            db.tx.events[values.eventIds[i]].update({
                                order: i,
                            })
                        );
                    }

                    // Unlink events that were removed from the form
                    if (!isCreating) {
                        const removedEventIds =
                            group?.events?.filter(oldEvent => !values.eventIds.includes(oldEvent.id)).map(event => event.id) || [];

                        if (removedEventIds.length > 0) {
                            txs.push(
                                db.tx.eventGroups[targetGroupId].unlink({
                                    events: removedEventIds,
                                })
                            );
                        }

                        // re-index the events
                        for (let i = 0; i < removedEventIds.length; i++) {
                            const eventId = removedEventIds[i];
                            // TODO: This will leave gaps in the unsorted event orders.
                            const newOrder = maxOrder + i + 1;
                            txs.push(db.tx.events[eventId].update({ order: newOrder }));
                        }
                    }

                    await db.transact(txs);

                    if (isCreating) {
                        navigate(`/group/${targetGroupId}`);
                    } else {
                        setEditMode(false);
                    }
                }}
            >
                {({ values, isSubmitting }) => {
                    return (
                        <>
                            <DiscardChangesDialog discard={discard} open={discardDialogOpen} onClose={closeDiscardDialog} />
                            <DirtyChecker dirty={hasBeenEdited} setDirty={setHasBeenEdited} />
                            <Form>
                                <Stack direction="column" spacing={2} sx={{ paddingTop: '10px' }}>
                                    {editMode && (
                                        <Field
                                            autoFocus={!groupId}
                                            required
                                            name="groupName"
                                            component={FormTextInput}
                                            label="Group name"
                                            fullWidth
                                        />
                                    )}

                                    <Typography variant="h5" sx={{ textAlign: 'center', mt: editMode ? 0 : 1 }}>
                                        Events
                                    </Typography>
                                    <EventSelectorList eventIds={values.eventIds} isSubmitting={isSubmitting} editMode={editMode} />

                                    {editMode && (
                                        <Stack direction="row" sx={{ justifyContent: 'center', mt: 2 }}>
                                            <Button type="submit" variant="contained" disabled={isSubmitting}>
                                                {isCreating ? 'Create Group' : 'Save All Changes'}
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </>
    );

    return <Box sx={containerSx}>{content}</Box>;
}
